import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import { Motion, spring, presets } from 'react-motion'
import raf from 'raf'
import ReactDOM from 'react-dom'
import classnames from 'classnames'
import StateMachine from 'javascript-state-machine'
import Hammer from 'hammerjs'
import tinycolor from 'tinycolor2'
import TimetoastPropTypes from '../../../../constants/timetoast_prop_types'
import FormattedTime from '@components/FormattedTime'
import { getReadableTextColor } from '@helpers/color_scheme_helpers.js'
import TimelineItemImage from './timeline_item_image'
import CloseButton from '@components/CloseButton'
import { clamp } from '../../../../utils/math'
import { ThemeContext } from '../../../../base/ThemeContext'
import { sanitize, markupAndSanitize } from '@utils/sanitize'

export default class Marker extends PureComponent {
  constructor(props) {
    super(props)
    this.hammerManager = null
    this.buttonHammerManager = null
    this.addedHammerListeners = false
    this.addedButtonHammerListeners = false
    this.animationId = null
    this._fsm = null
    this.createStateMachine()
    this.state = {
      animationState: this._fsm.current,
    }
    this._fsm.onenterstate = function (event, from, to, scope) {
      scope.enterStateHandler(scope, this)
    }
  }

  createStateMachine() {
    const { targetState } = this.props
    this._fsm = StateMachine.create({
      initial: targetState,
      error: function (eventName, from, to, args, errorCode, errorMessage) {
        // silently fail
      },
      events: [
        { name: 'expand', from: ['contracted', 'contracting'], to: 'expanding' },
        { name: 'expanded', from: 'expanding', to: 'expanded' },
        { name: 'contract', from: ['expanded', 'expanding'], to: 'contracting' },
        { name: 'contracted', from: 'contracting', to: 'contracted' },
      ],
    })
  }

  enterStateHandler(scope, fsmObject) {
    scope.setState({
      animationState: fsmObject.current,
    })
    this.lineUpRaf()
  }
  // Start state changing methods
  expand() {
    this._fsm.expand(this)
  }

  expanded() {
    this._fsm.expanded(this)
  }

  contract() {
    this._fsm.contract(this)
  }

  contracted() {
    this._fsm.contracted(this)
  }

  inTransitionState() {
    switch (this._fsm.current) {
      case 'expanding':
      case 'contracting':
        return true
        break

      default:
        return false
    }
  }

  // End state changing methods

  componentDidMount() {
    this.hammertime()
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.hammertime()

    const { targetState } = this.props
    const { targetState: prevTargetState } = prevProps
    if (targetState !== prevTargetState) {
      raf.cancel(this.animationId)
      if (targetState === 'expanded') {
        this.expand()
      } else if (targetState === 'contracted') {
        this.contract()
      }
    }
  }

  checkForAnimationStateChange() {
    const { animationState } = this.state
    if (this.inTransitionState()) {
      const { x, y, width, height, targetX, targetY, targetWidth, targetHeight, targetState } = this.props
      const reachedTarget = targetX === x && targetY === y && targetWidth === width && targetHeight === height
      if (reachedTarget) {
        raf.cancel(this.animationId)
        switch (this._fsm.current) {
          case 'expanding':
            this.expanded()
            break

          case 'contracting':
            this.contracted()
            break
        }
      } else {
        this.lineUpRaf()
      }
    } else {
      raf.cancel(this.animationId)
    }
  }

  lineUpRaf() {
    this.animationId = raf(() => {
      this.checkForAnimationStateChange()
    })
  }

  hammertime() {
    const { targetState } = this.props
    if (targetState === 'expanded') {
      if (this.hammerManager) {
        // when expanded remove hammer listeners so content can be scrolled on touch devices and text can be selected
        this.removeHammerListeners()
        this.removeHammerManager()
      }
    } else {
      if (!this.hammerManager) {
        this.hammerManager = new Hammer.Manager(ReactDOM.findDOMNode(this), {
          recognizers: [[Hammer.Pan, { threshold: 0, direction: Hammer.DIRECTION_HORIZONTAL }], [Hammer.Tap]],
        })
      }
      switch (targetState) {
        case 'contracted':
        case 'contracting':
          this.addHammerListeners()
          break
        case 'expanding':
        case 'expanded':
          this.removeHammerListeners()
          break
      }
    }
  }

  stopHammertime() {
    this.removeHammerListeners()
    this.removeHammerManager()
  }

  removeHammerManager() {
    if (this.hammerManager) {
      this.hammerManager.destroy()
      this.hammerManager = null
    }
  }

  addHammerListeners() {
    if (!this.addedHammerListeners && this.hammerManager) {
      this.hammerManager.on('tap', this.handleTap)
      this.hammerManager.on('panstart', this.handlePanStart)
      this.hammerManager.on('panmove', this.handlePanMove)
      this.hammerManager.on('panend', this.handlePanEnd)
      this.addedHammerListeners = true
    }
  }

  removeHammerListeners() {
    if (this.addedHammerListeners && this.hammerManager) {
      this.hammerManager.off('tap')
      this.hammerManager.off('panstart')
      this.hammerManager.off('panmove')
      this.hammerManager.off('panend')
      this.addedHammerListeners = false
    }
  }

  componentWillUnmount() {
    this.stopHammertime()
  }

  tapData() {
    const { marker } = this.props
    return {
      id: marker.id,
      timelineItemType: 'marker',
    }
  }

  handleTap = hammerEvent => {
    const { onTap, targetState } = this.props
    switch (targetState) {
      case 'contracted':
      case 'contracting':
        onTap(hammerEvent, this.tapData())
        break
    }
  }

  handleTapButton = hammerEvent => {
    const { targetState, onTapClose, onTap } = this.props
    switch (targetState) {
      case 'expanded':
      case 'expanding':
        onTapClose(hammerEvent, this.tapData())
        break
      case 'contracted':
      case 'contracting':
        onTap(hammerEvent, this.tapData())
        break
    }
  }

  getCloseButtonContent(x, y, zIndex, opacity) {
    const colorScheme = this.context
    const rotation = 90 - Math.round(90 * clamp(opacity, 0, 1))
    // Note: Always return close button with ref, whatever the content, so hammer can get a tap handler
    return (
      <CloseButton
        x={x}
        y={y}
        zIndex={zIndex}
        rotation={rotation}
        opacity={opacity}
        onTap={this.handleTapButton}
        colorScheme={colorScheme}
      />
    )
  }

  handlePanStart = hammerEvent => {
    this.props.onPanStart(hammerEvent)
  }

  handlePanMove = hammerEvent => {
    this.props.onPanMove(hammerEvent)
  }

  handlePanEnd = hammerEvent => {
    this.props.onPanEnd(hammerEvent)
  }

  showImage() {
    const { photoData } = this.props
    if (!photoData) return false
    // Could be a plain rest object or a JSON::API object
    const photoAttributes = photoData.attributes || photoData
    return photoAttributes && photoAttributes.image_processing_complete === true
  }

  getContractedContent(alpha, imageWidth) {
    const { marker, targetWidth, targetHeight } = this.props
    const colorScheme = this.context
    if (alpha > 0) {
      const padding = 8
      const backgroundColor = colorScheme.secondary
      const color = getReadableTextColor(backgroundColor, colorScheme)
      const dateTextColor = colorScheme.highlight
      const style = {
        opacity: alpha,
        width: targetWidth - imageWidth - padding * 2,
        height: targetHeight - padding * 2,
        position: 'absolute',
        left: imageWidth + padding,
        top: padding,
        overflow: 'hidden',
        zIndex: 1,
      }
      const textStyle = { color }
      return (
        <div className="contracted-content" style={style}>
          <div className="tooltip-title">
            <h1 style={textStyle}>
              <FormattedTime dateTime={marker.date} color={dateTextColor} />,{' '}
              <span dangerouslySetInnerHTML={{ __html: sanitize(marker.title) }}></span>
            </h1>
          </div>
        </div>
      )
    }
    return null
  }

  getExpandedContent(alpha) {
    const { marker, expandedWidth } = this.props
    const colorScheme = this.context
    if (alpha > 0) {
      const headerBackgroundColor = colorScheme.highlight
      const headerTextColor = getReadableTextColor(headerBackgroundColor, colorScheme)
      const headerStyle = { backgroundColor: headerBackgroundColor }
      const headerTextStyle = { color: headerTextColor }

      const tinyColor = tinycolor(headerTextColor)
      const gradientMiddleColor = tinyColor.setAlpha(0.75).toRgbString()
      const gradientEdgeColor = tinyColor.setAlpha(0.5).toRgbString()
      const headerHorizontalRuleStyle = {
        backgroundImage: `linear-gradient(to right, ${gradientEdgeColor}, ${gradientMiddleColor}, ${gradientEdgeColor})`,
      }
      const bodyBackgroundColor = colorScheme.secondary
      const bodyTextColor = getReadableTextColor(bodyBackgroundColor, colorScheme)

      const style = {
        opacity: alpha,
        width: expandedWidth,
        clear: 'both',
        zIndex: 1,
        backgroundColor: bodyBackgroundColor,
        color: bodyTextColor,
      }
      const description = marker.description || 'No description.'
      return (
        <div className="expanded-content" style={style}>
          <div className="tooltip-title" style={headerStyle}>
            <h1 style={headerTextStyle}>
              <FormattedTime dateTime={marker.date} />
              <hr style={headerHorizontalRuleStyle}></hr>
              <span dangerouslySetInnerHTML={{ __html: sanitize(marker.title) }}></span>
            </h1>
          </div>
          <p className="tooltip-description" dangerouslySetInnerHTML={{ __html: markupAndSanitize(description) }}></p>
        </div>
      )
    }
    return null
  }

  /* Alternative hardware accelerated marker positioning, but with less browser support.

  */
  render() {
    const { animationState } = this.state
    const {
      marker,
      x,
      y,
      width,
      height,
      targetWidth,
      targetHeight,
      expandedWidth,
      expandedHeight,
      photoData,
      photos,
      targetState,
      colorScheme,
    } = this.props
    // Note: Before moving to a css transform for positioning, we used to set left to x and top to y. We may wish to move to translate3D transform in future, but need to round to nearest pixel if we do
    const transformString = `translate(${x}px, ${y}px)`
    const timelineItemStyle = {
      width,
      height,
      left: 0,
      top: 0,
      WebkitTransform: transformString,
      MozTransform: transformString,
      msTransform: transformString,
      transform: transformString,
      display: 'block',
      backgroundColor: colorScheme.secondary,
      zIndex: 29, // behind modal
    }
    let holderClass,
      materialShadowClass,
      buttonContent,
      descriptionContent,
      contractedContentStyle,
      contractedTextOpacity,
      expandedTextOpacity,
      expandedContentStyle

    const showImage = this.showImage()
    let imageContainerWidth = 0
    let imageContainerHeight = 0
    let imageData = null

    // photoData could be a plain rest object or a JSON::API object
    const photoAttributes = photoData?.attributes || photoData

    let modalImageData = showImage ? photoAttributes?.image_urls?.medium : null

    let image = null
    let imageClickHandler = null
    let contractedContentAlpha
    let expandedContentAlpha

    let textStyle = {}
    let bodyStyle = {
      width: targetWidth,
      height: targetHeight,
    }
    let enableModalViewing = false
    let animationPreset
    switch (animationState) {
      case 'contracted':
        materialShadowClass = 'z-depth-1'
        holderClass = 'tooltip-contracted'
        textStyle.padding = 2
        if (showImage) {
          imageData = photoAttributes?.image_urls?.small_square
          imageContainerWidth = 45
          imageContainerHeight = 45
        } else {
          textStyle = {
            width: targetWidth,
          }
        }
        timelineItemStyle.zIndex = 29
        animationPreset = { stiffness: 200, damping: 23 }
        contractedContentAlpha = 1
        expandedContentAlpha = 0
        break

      case 'contracting':
        materialShadowClass = 'z-depth-1'
        holderClass = 'tooltip-contracted'
        textStyle.padding = 2
        timelineItemStyle.zIndex = 31
        if (showImage) {
          imageData = photoAttributes?.image_urls?.small_square
          imageContainerWidth = 45
          imageContainerHeight = 45
        } else {
          textStyle = {
            width: targetWidth,
          }
        }
        animationPreset = { stiffness: 200, damping: 23 }
        contractedContentAlpha = 1
        expandedContentAlpha = 0
        break

      case 'expanding':
      case 'expanded':
        materialShadowClass = 'z-depth-2'
        holderClass = 'tooltip-expanded'
        timelineItemStyle.zIndex = 32
        if (showImage) {
          imageData = photoAttributes?.image_urls?.medium_landscape
          imageContainerWidth = timelineItemStyle.width
          imageContainerHeight = (timelineItemStyle.width / 16) * 9
          imageClickHandler = this.handleImageClick
        }
        animationPreset = { stiffness: 180, damping: 23 }
        contractedContentAlpha = 0
        expandedContentAlpha = 1
        break
    }

    const contentHolderStyle = { width, height }
    return (
      <Motion
        key={`motion-marker-content-${marker.id}`}
        style={{
          imageContainerWidth: spring(imageContainerWidth, animationPreset),
          imageContainerHeight: spring(imageContainerHeight, animationPreset),
          contractedContentAlpha: spring(contractedContentAlpha, animationPreset),
          expandedContentAlpha: spring(expandedContentAlpha, animationPreset),
        }}
      >
        {interpolated => {
          if (showImage) {
            image = (
              <TimelineItemImage
                imageData={imageData}
                modalImageData={modalImageData}
                containerWidth={imageContainerWidth}
                containerHeight={imageContainerHeight}
                zIndex={2}
                enableModalViewing={animationState === 'expanded'}
              />
            )
          }
          const contractedContent = this.getContractedContent(interpolated.contractedContentAlpha, imageContainerWidth)
          const expandedContent = this.getExpandedContent(interpolated.expandedContentAlpha)
          const closeButton = this.getCloseButtonContent(x + width, y, 32, interpolated.expandedContentAlpha)
          return (
            <div>
              <div
                className={classnames('timeline-item', 'timeline-marker', materialShadowClass)}
                style={timelineItemStyle}
                key={`marker-${marker.id}`}
              >
                <div className={classnames('content-holder', holderClass)} style={contentHolderStyle}>
                  <div className="content-body" style={bodyStyle}>
                    {image}
                    {contractedContent}
                    {expandedContent}
                  </div>
                </div>
              </div>
              {closeButton}
            </div>
          )
        }}
      </Motion>
    )
  }
}
/*
const RestPhoto = PropTypes.shape({
  image_processing_complete: PropTypes.bool.isRequired,
  image_urls: PropTypes.shape({
    small_square: PropTypes.shape({
      src: PropTypes.string.isRequired,
      width: PropTypes.number,
      height: PropTypes.number,
    }).isRequired,
    medium_landscape: PropTypes.shape({
      src: PropTypes.string.isRequired,
      width: PropTypes.number,
      height: PropTypes.number,
    }).isRequired,
  }),
})

const JsonApiPhoto = PropTypes.shape({
  attributes: RestPhoto,
})
*/
Marker.contextType = ThemeContext
Marker.propTypes = {
  marker: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    date: PropTypes.string.isRequired,
  }),
  photoData: TimetoastPropTypes.photo,
  onTap: PropTypes.func.isRequired,
  onTapClose: PropTypes.func.isRequired,
  onPanStart: PropTypes.func.isRequired,
  onPanMove: PropTypes.func.isRequired,
  onPanEnd: PropTypes.func.isRequired,
  targetState: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
}
Marker.defaultProps = {
  onTap: noop,
  onTapClose: noop,
  onPanStart: noop,
  onPanMove: noop,
  onPanEnd: noop,
  targetState: 'contracted',
}
